import React, { useEffect, useMemo } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Clock, Banknote, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { getTopicBySlug, generateTopicArticle } from "../lib/blogContent";
import { BRAND } from "../mock";
import { useLang } from "../i18n/LanguageContext";
import { useSeo, SEO_BASE } from "../lib/seo";

const Topic = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "");
  const navigate = useNavigate();
  const { t } = useLang();
  const topic = getTopicBySlug(slug);
  const article = useMemo(() => (topic ? generateTopicArticle(topic) : null), [topic]);
  const heroImg = topic?.heroImg || "https://images.unsplash.com/photo-1715267174266-95519fabe0c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  useSeo(article ? {
    title: article.title,
    description: article.description,
    path: `/${slug}`,
    image: heroImg,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Service", name: article.h1, serviceType: topic.kw, provider: { "@type": "LocalBusiness", name: BRAND.name, telephone: BRAND.phone }, areaServed: "Schweiz", url: SEO_BASE + "/" + slug },
        { "@type": "FAQPage", mainEntity: article.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
        { "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SEO_BASE + "/" },
          { "@type": "ListItem", position: 2, name: "Ratgeber", item: SEO_BASE + "/ratgeber" },
          { "@type": "ListItem", position: 3, name: article.h1, item: SEO_BASE + "/" + slug },
        ] },
      ],
    },
  } : {});

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) document.title = article.title;
  }, [article]);
  useEffect(() => { if (!topic) navigate("/ratgeber", { replace: true }); }, [topic, navigate]);
  if (!topic || !article) return null;

  return (
    <div className="pt-[68px] min-h-screen bg-white">
      <div className="relative bg-[#0B1533] text-white overflow-hidden">
        <img src={heroImg} alt={article.h1} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/85 to-[#0B1533]/70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 font-body text-sm text-white/60 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> {t("blog.back")}</Link>
          <p className="font-body text-sm text-[#C9A24B] font-semibold mb-3">Flughafen Zürich · 24/7</p>
          <h1 className="font-heading font-bold tracking-tight text-3xl sm:text-5xl leading-[1.08]">{article.h1}</h1>
          <div className="flex flex-wrap gap-3 mt-6">
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><Banknote size={15} className="text-[#25D366]" /> Fixpreise</span>
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><Clock size={15} className="text-[#2B4BFF]" /> 24/7</span>
            <span className="inline-flex items-center gap-2 bg-white/[0.08] border border-white/10 rounded-full px-4 py-2 font-body text-sm"><ShieldCheck size={15} className="text-[#2B4BFF]" /> Flugverfolgung</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/booking" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#1E38D8] transition-colors">{t("hero.view")} <ArrowRight size={16} /></Link>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-white/10 transition-colors"><Phone size={16} /> {BRAND.phone}</a>
          </div>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {article.sections.map((s) => (
          <section key={s.id} className="mb-9">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-4">{s.h}</h2>
            {s.paras.map((p, i) => (<p key={i} className="font-body text-[#3d4257] leading-[1.8] mb-4">{p}</p>))}
            {s.id === "preise" && (
              <div className="overflow-x-auto rounded-2xl border border-[#E9E5DE] mt-5">
                <table className="w-full text-left">
                  <thead className="bg-[#F5F2ED]"><tr><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Strecke</th><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Distanz</th><th className="font-heading text-sm text-[#0B1533] px-4 py-3">Fahrzeit</th><th className="font-heading text-sm text-[#0B1533] px-4 py-3">ab (Economy)</th></tr></thead>
                  <tbody>
                    {article.routes.map((r) => (
                      <tr key={r.path} className="border-t border-[#E9E5DE] hover:bg-[#F5F2ED]/50">
                        <td className="px-4 py-3"><Link to={r.path} className="font-body text-sm text-[#2B4BFF] font-medium hover:underline">Zürich → {r.name}</Link></td>
                        <td className="font-body text-sm text-[#5A607A] px-4 py-3">{r.km} km</td>
                        <td className="font-body text-sm text-[#5A607A] px-4 py-3">{r.min} min</td>
                        <td className="font-body text-sm font-semibold text-[#0B1533] px-4 py-3">CHF {r.fare}.–</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}

        <section className="mb-10">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-4">FAQ</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {article.faq.map((f, i) => (
              <AccordionItem key={i} value={`f-${i}`} className="bg-[#F5F2ED] border border-[#E9E5DE] rounded-2xl px-5">
                <AccordionTrigger className="font-heading font-semibold text-[#0B1533] text-left hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="font-body text-[#5A607A] leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <div className="rounded-3xl bg-[#0B1533] p-8 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div><h3 className="font-heading font-bold text-2xl">{article.h1}</h3><p className="font-body text-white/60 mt-2">{t("blog.ctaSub")}</p></div>
          <Link to="/booking" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#1E38D8] transition-colors shrink-0">{t("hero.view")} <ArrowRight size={16} /></Link>
        </div>
      </article>
    </div>
  );
};

export default Topic;
